/**
 * 1crew3 - Main Application
 * Connect. Match. Work.
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Public Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
// Yacht Owner Pages
import OwnerDashboard from './pages/owner/Dashboard';
import CreateRequest from './pages/owner/CreateRequest';
import EditRequest from './pages/owner/EditRequest';
import MyRequests from './pages/owner/MyRequests';
import RequestDetails from './pages/owner/RequestDetails';
import MyYachts from './pages/owner/MyYachts';
import BillingDashboard from './pages/owner/BillingDashboard';

// Billing Pages
import BillingSuccess from './pages/billing/BillingSuccess';
import BillingCancel from './pages/billing/BillingCancel';

// Helper Pages
import HelperDashboard from './pages/helper/Dashboard';
import AvailableRequests from './pages/helper/AvailableRequests';
import HelperRequestDetails from './pages/helper/RequestDetails';
import MyServices from './pages/helper/MyServices';


// Shared Pages
import ProfilePage from './pages/shared/ProfilePage';
import ChatPage from './pages/shared/ChatPage';
import ConversationPage from './pages/shared/ConversationPage';
import NotificationsPage from './pages/shared/NotificationsPage';
import RatingsPage from './pages/shared/RatingsPage';

// Components
import ToastContainer from './components/common/ToastContainer';
import ErrorBoundary from './components/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';
import ConnectionStatus from './components/ConnectionStatus';

// Blog Pages
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';

/**
 * Protected Route wrapper
 */
function ProtectedRoute({ children, allowedTypes }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Admin users have access to all routes
  if (user.user_type === 'admin') {
    return children;
  }

  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * Redirect authenticated users based on their type
 */
function AuthRedirect({ children }) {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    // Admin goes to owner dashboard (has full access)
    if (user.user_type === 'admin') {
      return <Navigate to="/owner" replace />;
    }
    const dashboardPath = user.user_type === 'helper' ? '/helper' : '/owner';
    return <Navigate to={dashboardPath} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Password Reset Routes - standalone */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public Routes */}
      <Route path="/" element={
        <AuthRedirect>
          <LandingPage />
        </AuthRedirect>
      } />

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={
          <AuthRedirect>
            <LoginPage />
          </AuthRedirect>
        } />
        <Route path="/register" element={
          <AuthRedirect>
            <RegisterPage />
          </AuthRedirect>
        } />
      </Route>

      {/* Yacht Owner Routes */}
      <Route element={
        <ProtectedRoute allowedTypes={['yacht_owner']}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/owner" element={<OwnerDashboard />} />
        <Route path="/owner/request/new" element={<CreateRequest />} />
        <Route path="/owner/requests" element={<MyRequests />} />
        <Route path="/owner/requests/:id" element={<RequestDetails />} />
        <Route path="/owner/requests/:id/edit" element={<EditRequest />} />
        <Route path="/owner/yachts" element={<MyYachts />} />
        <Route path="/owner/billing" element={<BillingDashboard />} />
      </Route>

      {/* Billing Callback Routes (standalone, no layout) */}
      <Route path="/billing/success" element={
        <ProtectedRoute allowedTypes={['yacht_owner']}>
          <BillingSuccess />
        </ProtectedRoute>
      } />
      <Route path="/billing/cancel" element={
        <ProtectedRoute allowedTypes={['yacht_owner']}>
          <BillingCancel />
        </ProtectedRoute>
      } />

      {/* Helper Routes */}
      <Route element={
        <ProtectedRoute allowedTypes={['helper']}>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/helper" element={<HelperDashboard />} />
        <Route path="/helper/requests" element={<AvailableRequests />} />
        <Route path="/helper/requests/:id" element={<HelperRequestDetails />} />
        <Route path="/helper/services" element={<MyServices />} />
      </Route>

      {/* Shared Authenticated Routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/ratings" element={<RatingsPage />} />
        <Route path="/messages/:conversationId" element={<ConversationPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
      </Route>

      {/* Blog Routes (Public) */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const appContent = (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
          <ToastContainer />
          <InstallPrompt />
          <ConnectionStatus />
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );

  return (
    <HelmetProvider>
      <ErrorBoundary>
        {googleClientId ? (
          <GoogleOAuthProvider clientId={googleClientId}>
            {appContent}
          </GoogleOAuthProvider>
        ) : (
          appContent
        )}
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
